import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Helmet } from 'react-helmet-async';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const Gallery = () => {
    const [galleryItems, setGalleryItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [activeCategory, setActiveCategory] = useState('all');
    const [loading, setLoading] = useState(true);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [transformations, setTransformations] = useState([]);
    const [transformationsLoading, setTransformationsLoading] = useState(true);

    // Base URL for images (your backend server)
    const IMAGE_BASE_URL = 'https://api.crazynailss.com';

    const categories = [
        { id: 'all', name: 'All Work' },
        { id: 'nails', name: 'Nail Art' },
        { id: 'extensions', name: 'Nail Extensions' },
        { id: 'lashes', name: 'Eyelash Extensions' },
        { id: 'manicure', name: 'Manicure' },
        { id: 'pedicure', name: 'Pedicure' },
        { id: 'facials', name: 'Facials' }
    ];

    const stats = [
        { number: '500+', label: 'Nail Designs Created' },
        { number: '300+', label: 'Happy Clients' },
        { number: '50+', label: 'Nail Art Awards' },
        { number: '100%', label: 'Client Satisfaction' }
    ];

    // Slider settings for transformations - FULLY RESPONSIVE
    const transformationSliderSettings = {
        dots: true,
        infinite: transformations.length > 1,
        speed: 500,
        slidesToShow: 2,       
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 4000,
        pauseOnHover: true,
        arrows: true,
        adaptiveHeight: true,
        centerMode: false,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: true,
                }
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: false,
                    dots: true,
                    centerMode: false,
                }
            },
            {
                breakpoint: 480,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: false,
                    dots: true,
                    centerMode: false,
                }
            }
        ]
    };

    // Custom arrows for transformation slider
    const PrevArrow = ({ onClick }) => (
        <button
            onClick={onClick}
            className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white dark:bg-dark rounded-full shadow-md flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300 hidden md:flex"
        >
            <i className="fas fa-chevron-left text-sm"></i>
        </button>
    );

    const NextArrow = ({ onClick }) => (
        <button
            onClick={onClick}
            className="absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white dark:bg-dark rounded-full shadow-md flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300 hidden md:flex"
        >
            <i className="fas fa-chevron-right text-sm"></i>
        </button>
    );

    useEffect(() => {
        fetchGallery();
    }, []);

    useEffect(() => {
        filterGallery();
    }, [galleryItems, activeCategory]);

    const fetchGallery = async () => {
        try {
            const response = await api.get('/gallery');
            console.log('Gallery data from API:', response.data);
            setGalleryItems(response.data);
        } catch (error) {
            console.error('Failed to fetch gallery:', error);
            // Fallback to mock data for demo
            setGalleryItems([
                { id: 1, title: 'Floral Nail Art', description: 'Intricate floral design with 3D elements', category: 'nails', image_url: 'https://images.unsplash.com/photo-1604654894610-df63bc536371' },
                { id: 2, title: 'French Tip with Glitter', description: 'Classic French manicure with glitter gradient', category: 'nails', image_url: 'https://images.unsplash.com/photo-1512496015851-a90fb38ba796' },
                { id: 3, title: 'Volume Eyelash Extensions', description: 'Full volume eyelash extensions for dramatic look', category: 'lashes', image_url: 'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e' },
                { id: 4, title: 'Marble Nail Design', description: 'Elegant marble effect with gold foil accents', category: 'nails', image_url: 'https://images.unsplash.com/photo-1574098529590-f5d101f1c48a' }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const filterGallery = () => {
        if (activeCategory === 'all') {
            setFilteredItems(galleryItems);
        } else {
            setFilteredItems(galleryItems.filter(item => item.category === activeCategory));
        }
    };

    useEffect(() => {
        fetchTransformations();
    }, []);

    // Add this function to fetch transformations
    const fetchTransformations = async () => {
        try {
            const response = await api.get('/transformations?limit=10');
            // If API returns data, use it, otherwise use mock data
            if (response.data && response.data.length > 0) {
                setTransformations(response.data);
            } else {
                // Fallback mock data with multiple transformations
                setTransformations([
                    {
                        id: 1,
                        title: 'Nail Extension Transformation',
                        description: 'Client received gel nail extensions with intricate floral nail art design.',
                        before_image: null,
                        after_image: null,
                        category: 'nails',
                        tags: ['Gel Extensions', 'Nail Art', 'French Tips']
                    },
                    {
                        id: 2,
                        title: 'Eyelash Extension Transformation',
                        description: 'Volume eyelash extensions applied for a dramatic, eye-opening effect.',
                        before_image: null,
                        after_image: null,
                        category: 'lashes',
                        tags: ['Volume Lashes', 'Eye Enhancement']
                    },
                    {
                        id: 3,
                        title: 'Manicure & Pedicure Transformation',
                        description: 'Complete hand and foot care treatment with premium products.',
                        before_image: null,
                        after_image: null,
                        category: 'manicure',
                        tags: ['Manicure', 'Pedicure', 'Spa Treatment']
                    },
                    {
                        id: 4,
                        title: 'Facial & Skin Transformation',
                        description: 'Rejuvenating facial treatment for glowing, healthy skin.',
                        before_image: null,
                        after_image: null,
                        category: 'facials',
                        tags: ['Facial', 'Skin Care', 'Glowing Skin']
                    }
                ]);
            }
        } catch (error) {
            console.error('Failed to fetch transformations:', error);
            // Fallback mock data with multiple transformations
            setTransformations([
                {
                    id: 1,
                    title: 'Nail Extension Transformation',
                    description: 'Client received gel nail extensions with intricate floral nail art design.',
                    before_image: null,
                    after_image: null,
                    category: 'nails',
                    tags: ['Gel Extensions', 'Nail Art', 'French Tips']
                },
                {
                    id: 2,
                    title: 'Eyelash Extension Transformation',
                    description: 'Volume eyelash extensions applied for a dramatic, eye-opening effect.',
                    before_image: null,
                    after_image: null,
                    category: 'lashes',
                    tags: ['Volume Lashes', 'Eye Enhancement']
                },
                {
                    id: 3,
                    title: 'Manicure & Pedicure Transformation',
                    description: 'Complete hand and foot care treatment with premium products.',
                    before_image: null,
                    after_image: null,
                    category: 'manicure',
                    tags: ['Manicure', 'Pedicure', 'Spa Treatment']
                },
                {
                    id: 4,
                    title: 'Facial & Skin Transformation',
                    description: 'Rejuvenating facial treatment for glowing, healthy skin.',
                    before_image: null,
                    after_image: null,
                    category: 'facials',
                    tags: ['Facial', 'Skin Care', 'Glowing Skin']
                }
            ]);
        } finally {
            setTransformationsLoading(false);
        }
    };

    // Function to get correct image URL
    const getImageUrl = (imageUrl) => {
        if (!imageUrl) return null;

        // If it's already a full URL (starts with http), return as is
        if (imageUrl.startsWith('http')) {
            return imageUrl;
        }

        // If it's a local path (starts with /uploads), prepend base URL
        if (imageUrl.startsWith('/uploads')) {
            return `${IMAGE_BASE_URL}${imageUrl}`;
        }

        // Otherwise, assume it's a local path
        return `${IMAGE_BASE_URL}/uploads/gallery/${imageUrl}`;
    };

    const openLightbox = (index) => {
        setCurrentIndex(index);
        setCurrentImage(filteredItems[index]);
        setLightboxOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        setCurrentImage(null);
        document.body.style.overflow = 'auto';
    };

    const nextImage = () => {
        const nextIndex = (currentIndex + 1) % filteredItems.length;
        setCurrentIndex(nextIndex);
        setCurrentImage(filteredItems[nextIndex]);
    };

    const prevImage = () => {
        const prevIndex = (currentIndex - 1 + filteredItems.length) % filteredItems.length;
        setCurrentIndex(prevIndex);
        setCurrentImage(filteredItems[prevIndex]);
    };

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!lightboxOpen) return;
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowLeft') prevImage();
            if (e.key === 'ArrowRight') nextImage();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxOpen, currentIndex]);

    const categoryDisplayNames = {
        nails: 'Nail Art',
        extensions: 'Nail Extensions',
        lashes: 'Eyelash Extensions',
        manicure: 'Manicure',
        pedicure: 'Pedicure',
        facials: 'Facials'
    };

    // Get transformation image URL
    const getTransformationImageUrl = (imageUrl) => {
        if (!imageUrl) return null;
        if (imageUrl.startsWith('http')) return imageUrl;
        if (imageUrl.startsWith('/uploads')) return `${IMAGE_BASE_URL}${imageUrl}`;
        return `${IMAGE_BASE_URL}/uploads/transformations/${imageUrl}`;
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Gallery | Crazy Nails</title>
                <meta name="description" content="Explore our portfolio of nail art, eyelash extensions, and beauty transformations. See why we're the preferred choice for premium beauty services." />
            </Helmet>

            {/* Page Header */}
            <section className="bg-gradient-to-r from-dark to-dark-light text-white py-28 text-center mt-20 relative overflow-hidden">
                <div className="absolute inset-0 bg-black/50"></div>
                <div className="container mx-auto px-4 max-w-7xl relative z-10">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">Our Work Gallery</h1>
                    <p className="text-white/90 text-lg max-w-2xl mx-auto">Explore our portfolio of nail art, eyelash extensions, and beauty transformations</p>
                </div>
            </section>

            {/* Category Filters */}
            <section className="py-6 bg-white dark:bg-dark border-b border-light-gray dark:border-gray-700 sticky top-20 z-30">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="flex flex-wrap gap-2 justify-center">
                        {categories.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => setActiveCategory(cat.id)}
                                className={`px-5 py-2 rounded-full font-medium transition-all ${activeCategory === cat.id
                                    ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-medium'
                                    : 'bg-white dark:bg-dark-light text-dark dark:text-white border border-light-gray dark:border-gray-700 hover:border-primary'
                                    }`}
                            >
                                {cat.name}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Gallery Grid */}
            <section className="py-16 bg-white dark:bg-dark">
                <div className="container mx-auto px-4 max-w-7xl">
                    {filteredItems.length === 0 ? (
                        <div className="text-center py-16">
                            <i className="fas fa-images text-6xl text-gray-300 mb-4"></i>
                            <h3 className="text-xl font-semibold mb-2">No gallery items found</h3>
                            <p className="text-gray">Check back soon for more beautiful work!</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredItems.map((item, index) => {
                                const imageUrl = getImageUrl(item.image_url);
                                console.log(`Image ${item.id} URL:`, imageUrl);

                                return (
                                    <div
                                        key={item.id}
                                        className="group relative rounded-2xl overflow-hidden cursor-pointer bg-light dark:bg-dark-light aspect-square"
                                        onClick={() => openLightbox(index)}
                                    >
                                        {imageUrl ? (
                                            <img
                                                src={imageUrl}
                                                alt={item.title}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                onError={(e) => {
                                                    console.error(`Failed to load image: ${imageUrl}`);
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><i class="fas fa-image text-5xl text-primary"></i></div>';
                                                }}
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <i className="fas fa-image text-5xl text-primary"></i>
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-5">
                                            <span className="text-white text-xs bg-primary px-2 py-1 rounded-full inline-block w-fit mb-2">
                                                {categoryDisplayNames[item.category] || item.category}
                                            </span>
                                            <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                                            <p className="text-white/80 text-sm">{item.description}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Stats Section */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-8 border-t border-light-gray dark:border-gray-700">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center p-6 bg-light dark:bg-dark-light rounded-xl hover:shadow-soft transition-all hover:-translate-y-1">
                                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">{stat.number}</div>
                                <p className="text-dark dark:text-white font-medium mb-0">{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Before & After Section */}
            <section className="py-16 bg-light dark:bg-dark-light">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="section-title">
                        <h2 className="text-2xl md:text-3xl font-bold">Transformations</h2>
                        <p>See the amazing before and after results from our beauty treatments</p>
                    </div>

                    {transformationsLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="loading-spinner"></div>
                        </div>
                    ) : transformations.length === 0 ? (
                        <div className="text-center py-12">
                            <i className="fas fa-images text-5xl text-gray-300 mb-4"></i>
                            <p className="text-gray">No transformations available yet.</p>
                        </div>
                    ) : (
                        <div className="mt-8 relative px-2 sm:px-4 md:px-8">
                            <Slider 
                                {...transformationSliderSettings}
                                prevArrow={<PrevArrow />}
                                nextArrow={<NextArrow />}
                            >
                                {transformations.map((transformation) => (
                                    <div key={transformation.id} className="px-2 md:px-3">
                                        <div className="bg-white dark:bg-dark rounded-2xl overflow-hidden shadow-soft hover:shadow-medium transition-all h-full">
                                            <div className="grid grid-cols-2">
                                                {/* Before Image */}
                                                <div className="p-3 md:p-5 text-center border-r border-light-gray dark:border-gray-700">
                                                    <h4 className="font-semibold mb-2 md:mb-3 text-xs md:text-sm">Before</h4>
                                                    <div className="aspect-square bg-accent dark:bg-primary/20 rounded-xl overflow-hidden">
                                                        {transformation.before_image ? (
                                                            <img
                                                                src={getTransformationImageUrl(transformation.before_image)}
                                                                alt="Before"
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                     e.target.src = '/logo512.png';
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex flex-col items-center justify-center">
                                                                <i className="fas fa-image text-2xl md:text-4xl text-primary mb-1 md:mb-2"></i>
                                                                <p className="text-gray text-xs md:text-sm mb-0">Before</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* After Image */}
                                                <div className="p-3 md:p-5 text-center">
                                                    <h4 className="font-semibold mb-2 md:mb-3 text-xs md:text-sm">After</h4>
                                                    <div className="aspect-square bg-accent dark:bg-primary/20 rounded-xl overflow-hidden">
                                                        {transformation.after_image ? (
                                                            <img
                                                                src={getTransformationImageUrl(transformation.after_image)}
                                                                alt="After"
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                     e.target.src = '/logo512.png';
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex flex-col items-center justify-center">
                                                                <i className="fas fa-image text-2xl md:text-4xl text-primary mb-1 md:mb-2"></i>
                                                                <p className="text-gray text-xs md:text-sm mb-0">After</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="p-3 md:p-5 border-t border-light-gray dark:border-gray-700">
                                                <h3 className="font-bold mb-1 md:mb-2 text-sm md:text-lg">{transformation.title}</h3>
                                                <p className="text-gray text-xs md:text-sm mb-2 md:mb-3 line-clamp-2">{transformation.description}</p>
                                                <div className="flex flex-wrap gap-1 md:gap-2">
                                                    {transformation.tags && transformation.tags.map((tag, idx) => (
                                                        <span key={idx} className="text-[10px] md:text-xs bg-accent dark:bg-primary/20 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full">
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </Slider>
                            
                            {/* Custom Dots Styling */}
                            <style jsx>{`
                                .slick-dots {
                                    bottom: -40px !important;
                                }
                                .slick-dots li button:before {
                                    font-size: 10px !important;
                                    color: #CBD5E1 !important;
                                    opacity: 1 !important;
                                }
                                .slick-dots li.slick-active button:before {
                                    color: #C8A165 !important;
                                    opacity: 1 !important;
                                }
                                @media (max-width: 767px) {
                                    .slick-dots {
                                        bottom: -35px !important;
                                    }
                                    .slick-dots li button:before {
                                        font-size: 12px !important;
                                    }
                                    .slick-dots li.slick-active button:before {
                                        font-size: 14px !important;
                                    }
                                }
                            `}</style>
                        </div>
                    )}
                </div>
            </section>

            {/* Instagram Feed */}
            <section className="py-16 bg-white dark:bg-dark">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="section-title">
                        <h2 className="text-2xl md:text-3xl font-bold">Follow Us on Instagram</h2>
                        <p>See more of our work and stay updated with the latest trends</p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mt-8">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="group relative aspect-square bg-accent dark:bg-primary/20 rounded-xl overflow-hidden cursor-pointer">
                                <div className="w-full h-full flex items-center justify-center">
                                    <i className="fas fa-heart text-3xl text-primary group-hover:scale-110 transition-transform"></i>
                                </div>
                                <div className="absolute inset-0 bg-primary/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                                    <span className="text-white text-sm font-medium">@crazy.nails.bangalore</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="text-center mt-8">
                        <a href="https://www.instagram.com/p/DbRCOTIE9Ve/?img_index=1" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-medium transition-all">
                            <i className="fab fa-instagram"></i> Follow @crazy.nails.bangalore
                        </a>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-gradient-to-r from-primary to-secondary text-white text-center">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-white text-3xl md:text-4xl font-bold mb-4">Inspired by Our Work?</h2>
                    <p className="text-white/90 text-lg mb-8">Book an appointment to get your own beautiful transformation</p>
                    <Link to="/booking" className="btn bg-white hover:bg-accent">Book Your Appointment Now</Link>
                </div>
            </section>

            {/* Lightbox Modal */}
            {lightboxOpen && currentImage && (
                <div className="fixed inset-0 z-[2000] bg-black/95 flex items-center justify-center">
                    <button onClick={closeLightbox} className="absolute top-5 right-5 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white text-2xl hover:bg-white/20 transition-all">
                        <i className="fas fa-times"></i>
                    </button>
                    <button onClick={prevImage} className="absolute left-5 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white text-2xl hover:bg-white/20 transition-all">
                        <i className="fas fa-chevron-left"></i>
                    </button>
                    <button onClick={nextImage} className="absolute right-5 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white text-2xl hover:bg-white/20 transition-all">
                        <i className="fas fa-chevron-right"></i>
                    </button>
                    <div className="max-w-4xl w-full mx-4">
                        <img
                            src={getImageUrl(currentImage.image_url)}
                            alt={currentImage.title}
                            className="w-full h-auto rounded-2xl"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/logo512.png';
                            }}
                        />
                        <div className="mt-4 text-center">
                            <h3 className="text-white text-xl font-semibold mb-2">{currentImage.title}</h3>
                            <p className="text-white/70">{currentImage.description}</p>
                            <span className="inline-block mt-2 text-primary text-sm">{categoryDisplayNames[currentImage.category]}</span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Gallery;